import { toCents, percentOfCents, addCents } from '../../../helpers/precision';
import { OR_750_TO_FIN_RATIO, SOURCE_TYPES } from './constants';

/**
 * Formatte un nombre avec 2 décimales
 */
export const formatNumber = (n) => (n === 0 || n ? Number(n).toFixed(2) : '');

/**
 * Sanitize un nombre d'entrée utilisateur
 */
export const sanitizeNumber = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const s = String(val).replace(/,/g, '.').trim();
  if (s === '') return null;
  const num = parseFloat(s);
  if (isNaN(num)) return null;
  return Number(num.toFixed(2));
};

/**
 * Formate une date ISO en format DD/MM/YYYY
 */
export const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, dd] = isoDate.split('-');
  return `${dd}/${m}/${y}`;
};

/**
 * Calcule les montants en centimes pour un mouvement
 * @param {Object} row - La ligne de mouvement
 * @param {boolean} isGold - Si c'est de l'or (métal_id = 1)
 * @param {boolean} isTabClal - Si on est dans l'onglet CLAL (pour inverser la logique fournisseur)
 */
export const calculateMovementCents = (row, isGold, isTabClal = false) => {
  let entreeFinCents = 0;
  let sortieFinCents = 0;
  let perteFinCents = 0;

  if (row.__source === SOURCE_TYPES.FOURNISSEUR) {
    const entreeOrFin = parseFloat((row.entree_or_fin != null ? row.entree_or_fin : row.entree_fin) || 0) || 0;
    const sortie750 = parseFloat(row.sortie_or_750 || 0) || 0;
    const pertePct = typeof row.perte === 'number' ? row.perte : Number(row.perte || 0);
    
    if (isTabClal) {
      // CONTEXTE CLAL: entree_or_fin = sortie CLAL (on donne au fournisseur)
      if (entreeOrFin > 0) {
        sortieFinCents = toCents(entreeOrFin);
        perteFinCents = 0;
      } else if (sortie750 > 0) {
        // sortie_or_750 = entrée CLAL (le fournisseur nous rend)
        if (isGold) {
          const sortieFinValue = sortie750 * OR_750_TO_FIN_RATIO;
          const perteFinValue = sortieFinValue * (pertePct / 100);
          entreeFinCents = toCents(sortieFinValue);
          perteFinCents = perteFinValue > 0 ? toCents(perteFinValue) : 0;
        } else {
          entreeFinCents = toCents(sortie750);
          const perteFinValue = sortie750 * (pertePct / 100);
          perteFinCents = perteFinValue > 0 ? toCents(perteFinValue) : 0;
        }
      }
    } else {
      // CONTEXTE FOURNISSEUR: entree_or_fin = entrée, sortie_or_750 = sortie
      if (isGold) {
        const sortieFinValue = sortie750 * OR_750_TO_FIN_RATIO;
        const perteFinValue = sortieFinValue * (pertePct / 100);
        entreeFinCents = toCents(entreeOrFin);
        sortieFinCents = toCents(sortieFinValue);
        perteFinCents = perteFinValue > 0 ? toCents(perteFinValue) : 0;
      } else {
        entreeFinCents = toCents(entreeOrFin);
        sortieFinCents = toCents(sortie750);
        const perteFinValue = sortie750 * (pertePct / 100);
        perteFinCents = perteFinValue > 0 ? toCents(perteFinValue) : 0;
      }
    }
  } else if (row.__source === SOURCE_TYPES.CLIENT) {
    const entreeOrFin = parseFloat((row.entree_or_fin != null ? row.entree_or_fin : row.entree_fin) || 0) || 0;
    const sortieOrFin = parseFloat((row.sortie_or_fin != null ? row.sortie_or_fin : row.sortie_fin) || 0) || 0;
    entreeFinCents = toCents(entreeOrFin);
    sortieFinCents = toCents(sortieOrFin);
    perteFinCents = 0;
  } else if (row.__source === SOURCE_TYPES.CLAL) {
    const entreeOrFin = parseFloat((row.entree_or_fin != null ? row.entree_or_fin : row.entree_fin) || 0) || 0;
    const sortieOrFin = parseFloat((row.sortie_or_fin != null ? row.sortie_or_fin :
      (row.sortie_fin != null ? row.sortie_fin : row.sortie_or_750)) || 0) || 0;
    entreeFinCents = toCents(entreeOrFin);
    sortieFinCents = toCents(sortieOrFin);
    perteFinCents = percentOfCents(entreeFinCents, row.perte || 0);
  }

  return { entreeFinCents, sortieFinCents, perteFinCents };
};

/**
 * Calcule les soldes progressifs par métal
 */
export const calculateProgressiveSoldes = (movements) => {
  const metalGroups = {};
  
  movements.forEach(row => {
    const metalId = String(row.metal_id || '1');
    if (!metalGroups[metalId]) metalGroups[metalId] = [];
    metalGroups[metalId].push(row);
  });

  Object.keys(metalGroups).forEach(metalId => {
    const chronological = [...metalGroups[metalId]].sort((a, b) => {
      if (a.date_mouvement === b.date_mouvement) return (a.id || 0) - (b.id || 0);
      return a.date_mouvement.localeCompare(b.date_mouvement);
    });

    let soldeCumulatif = 0;
    chronological.forEach(row => {
      const variation = row.__entreeFinCents - row.__sortieFinCents - row.__perteFinCents;
      soldeCumulatif = addCents(soldeCumulatif, variation);
      row.__soldeMetalCents = soldeCumulatif;
    });
  });
};

/**
 * Calcule les sommes pour un groupe de mouvements
 */
export const calculateSums = (rows) => {
  return rows.reduce((acc, r) => { 
    acc.entree += r.__entreeFinCents; 
    acc.sortie += r.__sortieFinCents; 
    acc.sortie750 += toCents(parseFloat(r.sortie_or_750 || 0)); 
    acc.perte += r.__perteFinCents; 
    return acc; 
  }, { 
    entree: 0, 
    sortie: 0, 
    sortie750: 0, 
    perte: 0, 
    solde: rows.length > 0 ? rows[0].__soldeMetalCents : 0 
  });
};

/**
 * Filtre les mouvements selon les critères
 */
export const filterMovements = (movements, filters) => {
  return movements.filter(row => {
    if (filters.fournisseurId && row.__source === SOURCE_TYPES.FOURNISSEUR && 
        String(row.fournisseur_id) !== String(filters.fournisseurId)) return false;
    
    if (filters.clientId && row.__source === SOURCE_TYPES.CLIENT && 
        String(row.client_id) !== String(filters.clientId)) return false;
    
    if (filters.metalId && String(row.metal_id) !== String(filters.metalId)) return false;
    
    if (filters.designation) {
      const d = (row.designation || '').toLowerCase();
      if (!d.includes(filters.designation.toLowerCase())) return false;
    }
    
    if (filters.dateDebut && row.date_mouvement < filters.dateDebut) return false;
    if (filters.dateFin && row.date_mouvement > filters.dateFin) return false;

    if (filters.achat === '1' && !row.achat) return false;
    if (filters.achat === '0' && row.achat) return false;
    
    return true;
  });
};

/**
 * Prépare les données pour l'onglet actuel
 */
export const prepareTabData = (rawData, tab) => {
  if (tab === 1) return rawData.fournisseursRows;
  if (tab === 2) return rawData.clientsRows;
  
  // Tab 0 (CLAL): combiner les sources pertinentes
  const fournisseursForClal = rawData.fournisseursRows.filter(r => 
    (parseFloat(r.entree_or_fin != null ? r.entree_or_fin : r.entree_fin) || 0) > 0
  );
  
  const clientsForClal = rawData.clientsRows.filter(r => {
    const entree = parseFloat((r.entree_or_fin != null ? r.entree_or_fin : r.entree_fin) || 0) || 0;
    const sortie = parseFloat((r.sortie_or_fin != null ? r.sortie_or_fin : r.sortie_fin) || 0) || 0;
    return entree > 0 || sortie > 0;
  });
  
  return [...rawData.clalNorm, ...fournisseursForClal, ...clientsForClal];
};
